/**
 * Accordion list of saved chat service tokens with Edit and Delete actions.
 * Header: provider icon, token name, GPT model (chip), edit/delete icons.
 * Expanded: all token details as label: value.
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { getProviderImageUrl, getProviderLabel, getModelLabel } from './constants';
import type { SavedTokenDisplay } from './types';

/** Human-readable labels for raw token detail keys (expand section) */
const DETAIL_LABELS: Record<string, string> = {
  token_type: 'Type',
  name: 'Name',
  gpt_model: 'GPT Model',
  deploymentName: 'Deployment name',
  deploymentVersion: 'API version',
  embedding_model: 'Embedding model',
  endpoint: 'Endpoint',
  api_key: 'API key',
  vault_unique_id: 'Vault ID',
  giggso_vault_id: 'Giggso Vault ID',
};

/** Preferred order for known keys; companyId excluded from expanded view */
const DETAIL_KEY_ORDER = [
  'name',
  'token_type',
  'gpt_model',
  'endpoint',
  'deploymentVersion',
  'deploymentName',
  'embedding_model',
  'api_key',
  'vault_unique_id',
  'giggso_vault_id',
];

/** Keys to hide in expanded view (shown in accordion header instead) */
const KEYS_HIDDEN_IN_EXPANDED = ['name', 'tokenName'];

function getDetailLabel(key: string): string {
  return DETAIL_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Decode base64 API key; on failure return original string */
function decodeApiKey(encoded: string): string {
  try {
    return atob(encoded.trim());
  } catch {
    return encoded;
  }
}

/** Mask API key as first 2 + "..." + last 4 (of decoded key) */
function maskApiKey(encoded: string): string {
  const decoded = decodeApiKey(encoded);
  if (decoded.length <= 6) return '••••••';
  return `${decoded.slice(0, 2)}...${decoded.slice(-4)}`;
}

/** Return keys from raw in order; excludes companyId and token name (shown in header) */
function getOrderedDetailKeys(raw: Record<string, unknown>): string[] {
  const known = DETAIL_KEY_ORDER.filter(
    (k) => raw[k] != null && raw[k] !== '' && !KEYS_HIDDEN_IN_EXPANDED.includes(k)
  );
  const rest = Object.keys(raw).filter(
    (k) => k !== 'companyId' && !DETAIL_KEY_ORDER.includes(k) && !KEYS_HIDDEN_IN_EXPANDED.includes(k)
  );
  return [...known, ...rest];
}

interface SavedTokenListProps {
  tokens: SavedTokenDisplay[];
  onEdit: (token: SavedTokenDisplay) => void;
  onDelete: (token: SavedTokenDisplay) => void;
}

const SavedTokenList: React.FC<SavedTokenListProps> = ({
  tokens,
  onEdit,
  onDelete,
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);
  /** Accordion id for which API key is visible (full); null = all masked */
  const [showApiKeyFor, setShowApiKeyFor] = useState<string | null>(null);

  if (tokens.length === 0) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No chat services connected yet. Select a provider above to add one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {tokens.map((token, index) => {
        const raw = token.raw ?? {};
        const gptModel = (raw.gpt_model as string) ?? (raw.deploymentName as string) ?? '—';
        const keyId = token.vault_unique_id ?? token.displayLabel ?? String(index);
        const accordionId = `token-accordion-${keyId}`;

        return (
          <Accordion
            key={keyId}
            expanded={expanded === accordionId}
            onChange={(e, isExp) => setExpanded(isExp ? accordionId : false)}
            sx={{ '&:before': { display: 'none' }, boxShadow: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${accordionId}-content`}
              id={`${accordionId}-header`}
              sx={{ minHeight: 56, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 2 } }}
            >
              {/* Provider icon */}
              <Box
                component="img"
                src={getProviderImageUrl(token.token_type)}
                alt=""
                sx={{ width: 28, height: 28, objectFit: 'contain' }}
              />
              {/* Token name and model chip grouped next to each other */}
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {token.displayLabel || 'Unnamed'}
                </Typography>
                <Chip label={getModelLabel(gptModel)} size="small" variant="outlined" />
              </Box>
              {/* Edit – use component="div" to avoid nested button inside AccordionSummary (which is a button) */}
              <IconButton
                component="div"
                role="button"
                tabIndex={0}
                size="small"
                aria-label="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(token);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onEdit(token); } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              {/* Delete */}
              <IconButton
                component="div"
                role="button"
                tabIndex={0}
                size="small"
                aria-label="Delete"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(token);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onDelete(token); } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2, px: 3 }}>
              <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 24px', alignItems: 'baseline' }}>
                {getOrderedDetailKeys(raw).map((key) => {
                  const label = getDetailLabel(key);
                  let displayValue: React.ReactNode;
                  if (key === 'token_type') {
                    displayValue = getProviderLabel(raw[key] as string);
                  } else if (key === 'gpt_model') {
                    displayValue = getModelLabel(raw[key] as string);
                  } else if (key === 'api_key') {
                    const encoded = (raw[key] as string) ?? '';
                    const decoded = decodeApiKey(encoded);
                    const isVisible = showApiKeyFor === accordionId;
                    displayValue = (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography component="span" variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {encoded ? (isVisible ? decoded : maskApiKey(encoded)) : '—'}
                        </Typography>
                        {encoded && (
                          <IconButton
                            size="small"
                            aria-label={isVisible ? 'Hide API key' : 'Show API key'}
                            onClick={() => setShowApiKeyFor((prev) => (prev === accordionId ? null : accordionId))}
                            sx={{ p: 0.25 }}
                          >
                            {isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </Box>
                    );
                  } else {
                    const val = raw[key];
                    displayValue = val != null && val !== '' ? String(val) : '—';
                  }
                  const isDdBox = key === 'api_key';
                  return (
                    <React.Fragment key={key}>
                      <Typography component="dt" variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {label}:
                      </Typography>
                      {isDdBox ? (
                        <Box component="dd" sx={{ m: 0 }}>
                          {displayValue}
                        </Box>
                      ) : (
                        <Typography component="dd" variant="body2" sx={{ m: 0, wordBreak: 'break-all' }}>
                          {displayValue}
                        </Typography>
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default SavedTokenList;
